import { createSlice, createSelector, PayloadAction } from "@reduxjs/toolkit";
import { Workspace, CanvasWithData, PaperWithData, ReadingWithData, CanvasData } from "../../types/workspace";
import type { ArrowKind } from "../../types/canvasTypes";
import {
  Canvas,
  Category,
  Paper,
  ReadingWithUserStatus,
  SharedDataJSON,
  UserDataJSON,
  WorkspacePermissionWithAuthor,
} from "../../types/models";
import { DEFAULT_CATEGORIES } from "../../shared/utils/defaultCategories";
import { makeDefaultRubricSettings, type RubricSettings } from "../../shared/utils/defaultRubricSettings";
import type { IHighlight } from "@argument-studio/react-pdf-highlighter-with-categories";
import type { RootState } from "../index";

const initialState: Workspace = {
  workspace_id: "",
  workspace_name: "",
  shared_data: {
    categories: [...DEFAULT_CATEGORIES],
  },
  user_data: {
    timeLog: 0,
  },
  // Selection state
  selectedReadingId: null,
  selectedCanvasId: null,
  selectedPaperId: null,
  // Dirty tracking
  dirtyResources: {
    reading: false,
    canvas: false,
    paper: false,
  },
  // Resources
  readings: [],
  canvases: [],
  papers: [],
};

export const workspaceSlice = createSlice({
  name: "workspace",
  initialState,
  reducers: {
    // ========================================================================
    // SELECTION
    // ========================================================================

    setSelectedReading: (draft: Workspace, action: PayloadAction<string | null>) => {
      draft.selectedReadingId = action.payload;
    },

    setSelectedCanvas: (draft: Workspace, action: PayloadAction<string | null>) => {
      draft.selectedCanvasId = action.payload;
    },

    setSelectedPaper: (draft: Workspace, action: PayloadAction<string | null>) => {
      draft.selectedPaperId = action.payload;
    },

    clearAllSelections: (draft: Workspace) => {
      draft.selectedReadingId = null;
      draft.selectedCanvasId = null;
      draft.selectedPaperId = null;
      draft.dirtyResources.reading = false;
      draft.dirtyResources.canvas = false;
      draft.dirtyResources.paper = false;
    },

    // ========================================================================
    // DIRTY TRACKING
    // ========================================================================

    markResourceDirty: (
      draft: Workspace,
      action: PayloadAction<'reading' | 'canvas' | 'paper'>
    ) => {
      draft.dirtyResources[action.payload] = true;
    },

    markResourceClean: (
      draft: Workspace,
      action: PayloadAction<'reading' | 'canvas' | 'paper'>
    ) => {
      draft.dirtyResources[action.payload] = false;
    },

    markAllResourcesClean: (draft: Workspace) => {
      draft.dirtyResources.reading = false;
      draft.dirtyResources.canvas = false;
      draft.dirtyResources.paper = false;
    },

    // ========================================================================
    // WORKSPACE METADATA
    // ========================================================================

    setWorkspaceId: (draft: Workspace, action: PayloadAction<string>) => {
      draft.workspace_id = action.payload;
    },
    setWorkspaceName: (draft: Workspace, action: PayloadAction<string>) => {
      draft.workspace_name = action.payload;
    },
    setSharedData: (
      draft: Workspace,
      action: PayloadAction<{ categories: Category[] }>
    ) => {
      draft.shared_data.categories = action.payload.categories;
    },
    addCategory: (draft: Workspace, action: PayloadAction<Category>) => {
      const { label, background } = action.payload;
      if (!draft.shared_data.categories.find((cat) => cat.label === label)) {
        draft.shared_data.categories.push({
          label,
          background,
        });
      }
    },
    removeCategory: (draft: Workspace, action: PayloadAction<string>) => {
      const index = draft.shared_data.categories.findIndex(
        (cat) => cat.label === action.payload
      );
      if (index !== -1) {
        draft.shared_data.categories.splice(index, 1);
      }
    },
    overwriteCategories: (
      draft: Workspace,
      action: PayloadAction<{ categories: Category[] }>
    ) => {
      draft.shared_data.categories = action.payload.categories;
    },
    setUserData: (
      draft: Workspace,
      action: PayloadAction<{ timeLog: number }>
    ) => {
      draft.user_data.timeLog = action.payload.timeLog;
    },
    addReading: (draft: Workspace, action: PayloadAction<ReadingWithUserStatus>) => {
      if (!draft.readings.some(r => r.reading_id === action.payload.reading_id)) {
        const { reading_data, ...rest } = action.payload;
        const parsedReading: ReadingWithData = {
          ...rest,
          reading_data: reading_data ? JSON.parse(reading_data) : { highlights: [] },
        };
        draft.readings.push(parsedReading);
      }
    },
    setReadings: (draft: Workspace, action: PayloadAction<ReadingWithUserStatus[]>) => {
      // Item 7 Stage 3.6: stop parsing reading.reading_data. Highlights
      // are now loaded per-user from the annotations table by the
      // selectReading thunk. We seed each reading with an empty
      // highlights array; the thunk replaces it on selection.
      //
      // Preserve the in-memory highlights for the currently-selected
      // reading so a refresh of the workspace doesn't blow away
      // already-loaded annotations.
      const selectedId = draft.selectedReadingId;
      const previouslyLoaded = selectedId
        ? draft.readings.find((r) => r.reading_id === selectedId)?.reading_data
        : undefined;
      draft.readings = action.payload.map(reading => {
        const { reading_data: _legacyReadingData, ...rest } = reading;
        if (previouslyLoaded && reading.reading_id === selectedId) {
          return { ...rest, reading_data: previouslyLoaded };
        }
        return { ...rest, reading_data: { highlights: [] } };
      });
    },

    /**
     * Replace the highlights array for a specific reading. Used by the
     * selectReading thunk after it fetches the per-user annotations
     * from the backend (Stage 3.6).
     */
    setReadingHighlights: (
      draft: Workspace,
      action: PayloadAction<{ readingId: string; highlights: IHighlight[] }>
    ) => {
      const reading = draft.readings.find(
        (r) => r.reading_id === action.payload.readingId
      );
      if (!reading) return;
      reading.reading_data = { highlights: action.payload.highlights };
    },

    // ========================================================================
    // GRADING VIEW (Stage 3.7) — workspace-owner-only
    //
    // When `gradingView` is set on a reading, display selectors surface
    // the contributor's highlights instead of the owner's own. Mutation
    // thunks check this state and no-op (the owner shouldn't be editing
    // a contributor's annotations through the same UI).
    // ========================================================================

    // Stage 6.8: picker selection lives at workspace level. These
    // per-reading reducers only set/clear the loaded highlights cache.
    setGradingView: (
      draft: Workspace,
      action: PayloadAction<{ userId: string; username: string }>
    ) => {
      draft.gradingView = action.payload;
    },

    clearGradingView: (draft: Workspace) => {
      draft.gradingView = undefined;
      draft.gradingViewEvaluation = undefined;
      // Drop per-resource caches so a re-enter forces a refetch.
      for (const r of draft.readings) r.gradingView = undefined;
      for (const c of draft.canvases) c.gradingView = undefined;
    },

    setGradingViewEvaluation: (
      draft: Workspace,
      action: PayloadAction<{ enabled: boolean }>
    ) => {
      draft.gradingViewEvaluation = action.payload.enabled;
    },

    setGradeWeights: (
      draft: Workspace,
      action: PayloadAction<{ reader: number; canvas: number; editor: number }>
    ) => {
      draft.gradeWeights = {
        reader: Math.max(0, action.payload.reader),
        canvas: Math.max(0, action.payload.canvas),
        editor: Math.max(0, action.payload.editor),
      };
    },

    setEditorRubricCriteria: (
      draft: Workspace,
      action: PayloadAction<string[]>
    ) => {
      draft.editorRubricCriteria = action.payload;
    },

    setGradeDimensionWeight: (
      draft: Workspace,
      action: PayloadAction<
        | { section: "reader"; key: "completeness" | "categoryAccuracy"; value: number }
        | { section: "canvas"; key: "nodeCoverage" | "categoryAccuracy" | "edgeCoverage"; value: number }
        | { section: "editorCriterion"; idx: number; value: number }
      >
    ) => {
      if (!draft.gradeDimensionWeights) draft.gradeDimensionWeights = {};
      const w = Math.max(0, action.payload.value);
      if (action.payload.section === "reader") {
        if (!draft.gradeDimensionWeights.reader) draft.gradeDimensionWeights.reader = {};
        draft.gradeDimensionWeights.reader[action.payload.key] = w;
      } else if (action.payload.section === "canvas") {
        if (!draft.gradeDimensionWeights.canvas) draft.gradeDimensionWeights.canvas = {};
        draft.gradeDimensionWeights.canvas[action.payload.key] = w;
      } else {
        if (!draft.gradeDimensionWeights.editorCriteria) draft.gradeDimensionWeights.editorCriteria = [];
        const arr = draft.gradeDimensionWeights.editorCriteria;
        while (arr.length <= action.payload.idx) arr.push(1);
        arr[action.payload.idx] = w;
      }
    },

    setReadingGradingViewData: (
      draft: Workspace,
      action: PayloadAction<{
        readingId: string;
        highlights: IHighlight[];
      }>
    ) => {
      const reading = draft.readings.find(
        (r) => r.reading_id === action.payload.readingId
      );
      if (!reading) return;
      reading.gradingView = { highlights: action.payload.highlights };
    },

    setCanvasGradingViewData: (
      draft: Workspace,
      action: PayloadAction<{
        canvasId: string;
        canvasData: CanvasData;
      }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === action.payload.canvasId
      );
      if (!canvas) return;
      canvas.gradingView = { canvasData: action.payload.canvasData };
    },

    setReadingContributorsCache: (
      draft: Workspace,
      action: PayloadAction<{
        readingId: string;
        contributors: { user_id: string; username: string; annotation_count: number }[];
      }>
    ) => {
      const reading = draft.readings.find(
        (r) => r.reading_id === action.payload.readingId
      );
      if (!reading) return;
      reading.contributorsCache = {
        contributors: action.payload.contributors,
        loadedAt: Date.now(),
      };
    },

    setCanvasContributorsCache: (
      draft: Workspace,
      action: PayloadAction<{
        canvasId: string;
        contributors: { user_id: string; username: string; rectangle_count: number }[];
      }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === action.payload.canvasId
      );
      if (!canvas) return;
      canvas.contributorsCache = {
        contributors: action.payload.contributors,
        loadedAt: Date.now(),
      };
    },

    /**
     * Stage 5: timestamp the contributor's last successful assessment
     * auto-save for this reading. Drives the GradeSection "saved Xs
     * ago" indicator.
     */
    markAssessmentSaved: (
      draft: Workspace,
      action: PayloadAction<{ readingId: string; savedAt: number }>
    ) => {
      const reading = draft.readings.find(
        (r) => r.reading_id === action.payload.readingId
      );
      if (!reading) return;
      reading.assessmentMeta = { lastSavedAt: action.payload.savedAt };
    },
    removeReading: (draft: Workspace, action: PayloadAction<string>) => {
      const index = draft.readings.findIndex(
        (reading) => reading.reading_id === action.payload
      );
      if (index !== -1) {
        draft.readings.splice(index, 1);
      }
    },
    renameReading: (
      draft: Workspace,
      action: PayloadAction<{ readingId: string; readingName: string }>
    ) => {
      const reading = draft.readings.find(
        (r) => r.reading_id === action.payload.readingId
      );
      if (reading) {
        reading.reading_name = action.payload.readingName;
      }
    },
    addCanvas: (draft: Workspace, action: PayloadAction<Canvas>) => {
      if (
        !draft.canvases.find(
          (canvas) => canvas.canvas_id === action.payload.canvas_id
        )
      ) {
        const { canvas_data, ...rest } = action.payload;
        const parsedCanvas: CanvasWithData = {
          ...rest,
          canvas_data: canvas_data ? JSON.parse(canvas_data) : undefined,
        };
        draft.canvases.push(parsedCanvas);
      }
    },
    setCanvases: (draft: Workspace, action: PayloadAction<Canvas[]>) => {
      // Item 7 Stage 6.7: stop parsing canvas.canvas_data. Per-user
      // canvas blobs are now loaded from canvas_user_data by the
      // selectCanvas thunk. Mirrors setReadings's Stage 3.6 change.
      //
      // Preserve the locally-dirty selected canvas's already-loaded
      // canvas_data so a workspace refresh doesn't blow away the
      // user's in-progress edits (same race against
      // WorkspaceDataFetcher that setReadings guards against).
      const selectedId = draft.selectedCanvasId;
      const previouslyLoaded = selectedId
        ? draft.canvases.find((c) => c.canvas_id === selectedId)?.canvas_data
        : undefined;
      draft.canvases = action.payload.map(canvas => {
        const { canvas_data: _legacyCanvasData, ...rest } = canvas;
        if (previouslyLoaded && canvas.canvas_id === selectedId) {
          return { ...rest, canvas_data: previouslyLoaded };
        }
        return { ...rest, canvas_data: undefined };
      });
    },

    /**
     * Replace the canvas_data blob for a specific canvas. Used by the
     * selectCanvas thunk after it fetches the per-user canvas data
     * from the backend (Stage 6.7). Mirrors setReadingHighlights.
     */
    setCanvasData: (
      draft: Workspace,
      action: PayloadAction<{ canvasId: string; canvasData: CanvasData | undefined }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === action.payload.canvasId
      );
      if (!canvas) return;
      canvas.canvas_data = action.payload.canvasData;
    },
    removeCanvas: (draft: Workspace, action: PayloadAction<string>) => {
      const index = draft.canvases.findIndex(
        (canvas) => canvas.canvas_id === action.payload
      );
      if (index !== -1) {
        draft.canvases.splice(index, 1);
      }
    },
    renameCanvas: (
      draft: Workspace,
      action: PayloadAction<{ canvasId: string; canvasName: string }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === action.payload.canvasId
      );
      if (canvas) {
        canvas.canvas_name = action.payload.canvasName;
      }
    },
    addPaper: (draft: Workspace, action: PayloadAction<Paper>) => {
      if (!draft.papers.some(p => p.paper_id === action.payload.paper_id)) {
        const { paper_data, ...rest } = action.payload;
        const parsedPaper: PaperWithData = {
          ...rest,
          paper_data: paper_data ? JSON.parse(paper_data) : undefined,
        };
        draft.papers.push(parsedPaper);
      }
    },
    setPapers: (draft: Workspace, action: PayloadAction<Paper[]>) => {
      // Preserve the locally-dirty selected paper (see setReadings).
      const dirtyId =
        draft.dirtyResources.paper && draft.selectedPaperId
          ? draft.selectedPaperId
          : null;
      const localDirty = dirtyId
        ? draft.papers.find((p) => p.paper_id === dirtyId)
        : null;
      draft.papers = action.payload.map(paper => {
        if (localDirty && paper.paper_id === localDirty.paper_id) {
          return localDirty;
        }
        const { paper_data, ...rest } = paper;
        return {
          ...rest,
          paper_data: paper_data ? JSON.parse(paper_data) : undefined,
        };
      });
    },
    removePaper: (draft: Workspace, action: PayloadAction<string>) => {
      const index = draft.papers.findIndex(
        (paper) => paper.paper_id === action.payload
      );
      if (index !== -1) {
        draft.papers.splice(index, 1);
      }
    },
    renamePaper: (
      draft: Workspace,
      action: PayloadAction<{ paperId: string; paperName: string }>
    ) => {
      const paper = draft.papers.find(
        (p) => p.paper_id === action.payload.paperId
      );
      if (paper) {
        paper.paper_name = action.payload.paperName;
      }
    },

    // ========================================================================
    // READING MUTATIONS (operate on currently selected reading)
    // ========================================================================

    addHighlight: (draft: Workspace, action: PayloadAction<IHighlight>) => {
      const reading = draft.readings.find(
        (r) => r.reading_id === draft.selectedReadingId
      );
      if (reading?.reading_data) {
        reading.reading_data.highlights.push(action.payload);
        // Stage 3.6: highlight persistence is per-mutation (save*Highlight
        // thunks). Dirty-flag tracking is no longer used for highlights.
      }
    },

    deleteHighlight: (draft: Workspace, action: PayloadAction<{ id: string }>) => {
      const reading = draft.readings.find(
        (r) => r.reading_id === draft.selectedReadingId
      );
      if (reading?.reading_data) {
        const index = reading.reading_data.highlights.findIndex(
          (h) => h.id === action.payload.id
        );
        if (index !== -1) {
          reading.reading_data.highlights.splice(index, 1);
          // Stage 3.6: highlight persistence is per-mutation (save*Highlight
        // thunks). Dirty-flag tracking is no longer used for highlights.
        }
      }
    },

    changeHighlightCategory: (
      draft: Workspace,
      action: PayloadAction<{ id: string; category: string }>
    ) => {
      const reading = draft.readings.find(
        (r) => r.reading_id === draft.selectedReadingId
      );
      if (reading?.reading_data) {
        const highlight = reading.reading_data.highlights.find(
          (h) => h.id === action.payload.id
        );
        if (highlight) {
          highlight.comment.category = action.payload.category;
          // Stage 3.6: highlight persistence is per-mutation (save*Highlight
        // thunks). Dirty-flag tracking is no longer used for highlights.
        }
      }
    },

    changeHighlightComment: (
      draft: Workspace,
      action: PayloadAction<{ id: string; comment: string }>
    ) => {
      const reading = draft.readings.find(
        (r) => r.reading_id === draft.selectedReadingId
      );
      if (reading?.reading_data) {
        const highlight = reading.reading_data.highlights.find(
          (h) => h.id === action.payload.id
        );
        if (highlight) {
          highlight.comment.text = action.payload.comment;
          // Stage 3.6: highlight persistence is per-mutation (save*Highlight
        // thunks). Dirty-flag tracking is no longer used for highlights.
        }
      }
    },

    updateHighlight: (
      draft: Workspace,
      action: PayloadAction<{
        id: string;
        position: Partial<{ boundingRect: any; rects: any[]; pageNumber: number }>;
        content: Partial<{ text?: string; image?: string }>;
      }>
    ) => {
      const reading = draft.readings.find(
        (r) => r.reading_id === draft.selectedReadingId
      );
      if (reading?.reading_data) {
        const highlight = reading.reading_data.highlights.find(
          (h) => h.id === action.payload.id
        );
        if (highlight) {
          if (action.payload.position) {
            highlight.position = {
              ...highlight.position,
              ...action.payload.position,
            };
          }
          if (action.payload.content) {
            highlight.content = {
              ...highlight.content,
              ...action.payload.content,
            };
          }
          // Stage 3.6: highlight persistence is per-mutation (save*Highlight
        // thunks). Dirty-flag tracking is no longer used for highlights.
        }
      }
    },

    resetHighlights: (draft: Workspace) => {
      const reading = draft.readings.find(
        (r) => r.reading_id === draft.selectedReadingId
      );
      if (reading?.reading_data) {
        reading.reading_data.highlights = [];
        // Stage 3.6: highlight persistence is per-mutation (save*Highlight
        // thunks). Dirty-flag tracking is no longer used for highlights.
      }
    },

    // Removed: setPdfUrl and setPdfData - PDF URL is at top level (document_url), PDF data cached in IndexedDB

    addDemoHighlights: (draft: Workspace, action: PayloadAction<IHighlight[]>) => {
      const reading = draft.readings.find(
        (r) => r.reading_id === draft.selectedReadingId
      );
      if (reading?.reading_data) {
        reading.reading_data.highlights = action.payload;
        // Stage 3.6: highlight persistence is per-mutation (save*Highlight
        // thunks). Dirty-flag tracking is no longer used for highlights.
      }
    },

    // ========================================================================
    // RUBRIC MUTATIONS (operate on currently selected reading)
    //
    // Stage 1 of Item 7: in-memory only. Each setter takes a partial group
    // payload so RubricSection's onChange handlers can dispatch just the
    // changed field. The first mutation on a reading without a rubric
    // ensures the slot with default settings.
    // ========================================================================

    setRubricCompleteness: (
      draft: Workspace,
      action: PayloadAction<{ enabled?: boolean; penalty?: number }>
    ) => {
      const reading = draft.readings.find(
        (r) => r.reading_id === draft.selectedReadingId
      );
      if (!reading) return;
      if (!reading.rubric) {
        reading.rubric = { settings: makeDefaultRubricSettings(), expertHighlights: [] };
      }
      const { enabled, penalty } = action.payload;
      if (enabled !== undefined) reading.rubric.settings.completeness.enabled = enabled;
      if (penalty !== undefined) reading.rubric.settings.completeness.penalty = penalty;
    },

    setRubricCategoryMismatch: (
      draft: Workspace,
      action: PayloadAction<{ enabled?: boolean; penalty?: number }>
    ) => {
      const reading = draft.readings.find(
        (r) => r.reading_id === draft.selectedReadingId
      );
      if (!reading) return;
      if (!reading.rubric) {
        reading.rubric = { settings: makeDefaultRubricSettings(), expertHighlights: [] };
      }
      const { enabled, penalty } = action.payload;
      if (enabled !== undefined) reading.rubric.settings.categoryMismatch.enabled = enabled;
      if (penalty !== undefined) reading.rubric.settings.categoryMismatch.penalty = penalty;
    },

    /**
     * Stage 3.8: single match-threshold slider. Replaces the previous
     * setRubricOverlap reducer (which mutated both the enabled flag and
     * the threshold on a now-defunct `overlap` group).
     */
    setMatchThreshold: (
      draft: Workspace,
      action: PayloadAction<{ threshold: number }>
    ) => {
      const reading = draft.readings.find(
        (r) => r.reading_id === draft.selectedReadingId
      );
      if (!reading) return;
      if (!reading.rubric) {
        reading.rubric = { settings: makeDefaultRubricSettings(), expertHighlights: [] };
      }
      reading.rubric.settings.matchThreshold = action.payload.threshold;
    },

    /**
     * Stage 3.8 follow-up: toggle the rubric between "Recommended"
     * (use canonical defaults, hide knobs) and "Custom" (show knobs).
     * Toggling INTO recommended mode resets all three knobs to their
     * default values — that's what makes "recommended" mean exactly
     * what's stated in the UI rather than "whatever was last set."
     */
    setRubricUseRecommended: (
      draft: Workspace,
      action: PayloadAction<{ useRecommended: boolean }>
    ) => {
      const reading = draft.readings.find(
        (r) => r.reading_id === draft.selectedReadingId
      );
      if (!reading) return;
      if (!reading.rubric) {
        reading.rubric = { settings: makeDefaultRubricSettings(), expertHighlights: [] };
      }
      reading.rubric.settings.useRecommendedSettings = action.payload.useRecommended;
      if (action.payload.useRecommended) {
        // Snap values back to defaults so the "recommended" label
        // describes reality.
        const defaults = makeDefaultRubricSettings();
        reading.rubric.settings.completeness = defaults.completeness;
        reading.rubric.settings.categoryMismatch = defaults.categoryMismatch;
        reading.rubric.settings.matchThreshold = defaults.matchThreshold;
      }
    },

    /**
     * Hydrate a reading's rubric from a backend GET response. Takes an
     * explicit readingId rather than implicitly operating on
     * selectedReadingId so it stays correct if selection changes between
     * the fetch and the dispatch.
     *
     * Bumps loadGen so RubricSection's auto-save can distinguish a
     * hydration from a user edit — see the JSDoc on ReadingRubricData.
     */
    loadRubric: (
      draft: Workspace,
      action: PayloadAction<{
        readingId: string;
        settings: RubricSettings;
        expertHighlights: IHighlight[];
      }>
    ) => {
      const reading = draft.readings.find(
        (r) => r.reading_id === action.payload.readingId
      );
      if (!reading) return;
      const prevLoadGen = reading.rubric?.loadGen ?? 0;
      reading.rubric = {
        settings: action.payload.settings,
        expertHighlights: action.payload.expertHighlights,
        loadGen: prevLoadGen + 1,
      };
    },

    /**
     * Replace just the expert highlights for a reading (used by the
     * "Save current highlights as expert reference" admin button). Same
     * explicit-readingId pattern as loadRubric.
     */
    setExpertHighlights: (
      draft: Workspace,
      action: PayloadAction<{ readingId: string; highlights: IHighlight[] }>
    ) => {
      const reading = draft.readings.find(
        (r) => r.reading_id === action.payload.readingId
      );
      if (!reading) return;
      if (!reading.rubric) {
        reading.rubric = { settings: makeDefaultRubricSettings(), expertHighlights: [] };
      }
      reading.rubric.expertHighlights = action.payload.highlights;
    },

    /**
     * Stage 6.2: hydrate a canvas's rubric from a backend GET response.
     * Bumps loadGen so a downstream auto-save effect can suppress echo
     * if/when canvas rubric settings are user-editable.
     */
    loadCanvasRubric: (
      draft: Workspace,
      action: PayloadAction<{ canvasId: string; expertCanvas: CanvasData | null }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === action.payload.canvasId
      );
      if (!canvas) return;
      const prevLoadGen = canvas.rubric?.loadGen ?? 0;
      canvas.rubric = {
        expertCanvas: action.payload.expertCanvas,
        loadGen: prevLoadGen + 1,
      };
    },

    /**
     * Stage 6.2: replace just the expert canvas snapshot. Used by the
     * "Save current canvas as expert reference" admin button.
     */
    setExpertCanvas: (
      draft: Workspace,
      action: PayloadAction<{ canvasId: string; canvasData: CanvasData }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === action.payload.canvasId
      );
      if (!canvas) return;
      if (!canvas.rubric) {
        canvas.rubric = { expertCanvas: null };
      }
      canvas.rubric.expertCanvas = action.payload.canvasData;
    },

    /**
     * Stage 6.6: timestamp the contributor's last successful canvas
     * assessment auto-save. Drives the SavedAtChip on the Canvas
     * Grade card.
     */
    markCanvasAssessmentSaved: (
      draft: Workspace,
      action: PayloadAction<{ canvasId: string; savedAt: number }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === action.payload.canvasId
      );
      if (!canvas) return;
      canvas.assessmentMeta = { lastSavedAt: action.payload.savedAt };
    },

    // ========================================================================
    // CANVAS MUTATIONS (operate on currently selected canvas)
    // ========================================================================

    addRectangle: (draft: Workspace, action: PayloadAction<{ id: string; x: number; y: number; width: number; height: number; text: string; tag?: string; expanded?: boolean; arrows: any[]; source_annotation_client_id?: string }>) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (canvas?.canvas_data && !draft.gradingView) {
        canvas.canvas_data.rectangles.push(action.payload);
        draft.dirtyResources.canvas = true;
      }
    },

    deleteRectangle: (draft: Workspace, action: PayloadAction<{ idx: number }>) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (canvas?.canvas_data && !draft.gradingView) {
        const { idx } = action.payload;
        // Remove arrows pointing to this rectangle
        for (const rect of canvas.canvas_data.rectangles) {
          rect.arrows = rect.arrows.filter(
            (arrow) => arrow.endRectIdx !== idx
          );
        }
        canvas.canvas_data.rectangles.splice(idx, 1);
        // Reindex group memberships: drop the deleted index from every
        // group, then shift higher indices down by 1. A group that
        // ends up empty is removed. Mirrors the index-shift contract
        // arrows are supposed to hold; without this, group references
        // would silently drift after any delete.
        if (canvas.canvas_data.groups) {
          canvas.canvas_data.groups = canvas.canvas_data.groups
            .map((group) => ({
              ...group,
              memberRectIndices: group.memberRectIndices
                .filter((i) => i !== idx)
                .map((i) => (i > idx ? i - 1 : i)),
            }))
            .filter((group) => group.memberRectIndices.length > 0);
        }
        if (canvas.canvas_data.groupEdges) {
          const liveIds = new Set(
            (canvas.canvas_data.groups ?? []).map((g) => g.id)
          );
          canvas.canvas_data.groupEdges = canvas.canvas_data.groupEdges
            .filter((e) => e.toRectIdx !== idx)
            .map((e) =>
              e.toRectIdx !== undefined && e.toRectIdx > idx
                ? { ...e, toRectIdx: e.toRectIdx - 1 }
                : e
            )
            .filter(
              (e) =>
                liveIds.has(e.fromGroupId) &&
                (e.toGroupId === undefined || liveIds.has(e.toGroupId))
            );
        }
        draft.dirtyResources.canvas = true;
      }
    },

    deleteRectanglesBulk: (
      draft: Workspace,
      action: PayloadAction<{ indices: number[] }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (!canvas?.canvas_data || draft.gradingView) return;
      const toDelete = new Set(action.payload.indices);
      if (toDelete.size === 0) return;

      // Same compaction pattern as deleteGroupAndMembers: build a
      // survivors list with an old→new index map, then rewrite arrow
      // endpoints and group memberships through it. This is the
      // arrow-reindex behavior the single-rect deleteRectangle still
      // doesn't do (latent bug).
      const survivors: typeof canvas.canvas_data.rectangles = [];
      const indexMap = new Map<number, number>();
      canvas.canvas_data.rectangles.forEach((rect, oldIdx) => {
        if (toDelete.has(oldIdx)) return;
        indexMap.set(oldIdx, survivors.length);
        survivors.push(rect);
      });

      for (const rect of survivors) {
        rect.arrows = rect.arrows
          .filter((arrow) => !toDelete.has(arrow.endRectIdx))
          .map((arrow) => ({
            ...arrow,
            endRectIdx: indexMap.get(arrow.endRectIdx) ?? arrow.endRectIdx,
          }));
      }
      canvas.canvas_data.rectangles = survivors;

      if (canvas.canvas_data.groups) {
        canvas.canvas_data.groups = canvas.canvas_data.groups
          .map((g) => ({
            ...g,
            memberRectIndices: g.memberRectIndices
              .filter((i) => !toDelete.has(i))
              .map((i) => indexMap.get(i) ?? i),
          }))
          .filter((g) => g.memberRectIndices.length > 0);
      }

      if (canvas.canvas_data.groupEdges) {
        const liveIds = new Set(
          (canvas.canvas_data.groups ?? []).map((g) => g.id)
        );
        canvas.canvas_data.groupEdges = canvas.canvas_data.groupEdges
          .filter(
            (e) => e.toRectIdx === undefined || !toDelete.has(e.toRectIdx)
          )
          .map((e) =>
            e.toRectIdx !== undefined
              ? { ...e, toRectIdx: indexMap.get(e.toRectIdx) ?? e.toRectIdx }
              : e
          )
          .filter(
            (e) =>
              liveIds.has(e.fromGroupId) &&
              (e.toGroupId === undefined || liveIds.has(e.toGroupId))
          );
      }

      draft.dirtyResources.canvas = true;
    },

    translateRectanglesBulk: (
      draft: Workspace,
      action: PayloadAction<{ indices: number[]; dx: number; dy: number }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (!canvas?.canvas_data || draft.gradingView) return;
      const { indices, dx, dy } = action.payload;
      if (dx === 0 && dy === 0) return;
      for (const idx of indices) {
        const rect = canvas.canvas_data.rectangles[idx];
        if (rect) {
          rect.x += dx;
          rect.y += dy;
        }
      }
      draft.dirtyResources.canvas = true;
    },

    moveRectangle: (
      draft: Workspace,
      action: PayloadAction<{ idx: number; x: number; y: number }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (canvas?.canvas_data && !draft.gradingView) {
        const { idx, x, y } = action.payload;
        const rect = canvas.canvas_data.rectangles[idx];
        if (rect) {
          rect.x = x;
          rect.y = y;
          draft.dirtyResources.canvas = true;
        }
      }
    },

    editRectangle: (
      draft: Workspace,
      action: PayloadAction<{
        idx: number;
        width: number;
        height: number;
        textInput: string;
      }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (canvas?.canvas_data && !draft.gradingView) {
        const { idx, width, height, textInput } = action.payload;
        const rect = canvas.canvas_data.rectangles[idx];
        if (rect) {
          rect.expanded = false;
          rect.height = height;
          rect.width = width;
          rect.text = textInput;
          draft.dirtyResources.canvas = true;
        }
      }
    },

    toggleRectangleExpansion: (
      draft: Workspace,
      action: PayloadAction<{ idx: number; width: number; height: number }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (canvas?.canvas_data && !draft.gradingView) {
        const { idx, width, height } = action.payload;
        const rect = canvas.canvas_data.rectangles[idx];
        if (rect) {
          rect.expanded = !rect.expanded;
          rect.width = width;
          rect.height = height;
          draft.dirtyResources.canvas = true;
        }
      }
    },

    // ========================================================================
    // GROUPS
    // ========================================================================

    groupRectangles: (
      draft: Workspace,
      action: PayloadAction<{
        id: string;
        memberRectIndices: number[];
        name?: string;
        color?: string;
      }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (!canvas?.canvas_data || draft.gradingView) return;
      const { id, memberRectIndices, name, color } = action.payload;
      if (memberRectIndices.length === 0) return;

      // A rect belongs to at most one group. If any member is already
      // grouped, the caller (Canvas.tsx) should have refused — defensive
      // check here so a buggy caller can't create overlapping groups.
      const existing = canvas.canvas_data.groups ?? [];
      const alreadyGrouped = new Set<number>();
      for (const g of existing) {
        for (const i of g.memberRectIndices) alreadyGrouped.add(i);
      }
      if (memberRectIndices.some((i) => alreadyGrouped.has(i))) return;

      if (!canvas.canvas_data.groups) canvas.canvas_data.groups = [];
      canvas.canvas_data.groups.push({
        id,
        name,
        color,
        memberRectIndices: [...memberRectIndices].sort((a, b) => a - b),
      });
      draft.dirtyResources.canvas = true;
    },

    addGroupEdge: (
      draft: Workspace,
      action: PayloadAction<{
        fromGroupId: string;
        toGroupId?: string;
        toRectIdx?: number;
        kind?: ArrowKind;
        credence?: number;
        tag?: string;
      }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (!canvas?.canvas_data || draft.gradingView) return;
      const { fromGroupId, toGroupId, toRectIdx } = action.payload;
      if (toGroupId === undefined && toRectIdx === undefined) return;
      if (toGroupId === fromGroupId) return;
      const edges = (canvas.canvas_data.groupEdges ??= []);
      if (
        edges.some(
          (e) =>
            e.fromGroupId === fromGroupId &&
            e.toGroupId === toGroupId &&
            e.toRectIdx === toRectIdx
        )
      ) {
        return;
      }
      edges.push({
        fromGroupId,
        toGroupId,
        toRectIdx,
        // Undirected by default, matching drag-created rect arrows: a
        // fresh connection is scaffolding until the user commits to a
        // direction via the context menu.
        kind: action.payload.kind ?? "undirected",
        credence: action.payload.credence ?? 100,
        tag: action.payload.tag ?? "",
      });
      draft.dirtyResources.canvas = true;
    },

    // Reverse a group-to-group edge, landing it as *directed* toward the
    // old source (credence/tag kept) — mirrors the arrow menu's
    // "Directed -> {start}" behavior. Only meaningful for group targets;
    // a node-targeted edge has no representable reverse (edges originate
    // from groups only). If the reversed edge already exists, the
    // original is removed and the existing reverse becomes directed.
    flipGroupEdge: (
      draft: Workspace,
      action: PayloadAction<{ fromGroupId: string; toGroupId: string }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (!canvas?.canvas_data?.groupEdges || draft.gradingView) return;
      const { fromGroupId, toGroupId } = action.payload;
      const edges = canvas.canvas_data.groupEdges;
      const edge = edges.find(
        (e) => e.fromGroupId === fromGroupId && e.toGroupId === toGroupId
      );
      if (!edge) return;
      const reverse = edges.find(
        (e) => e.fromGroupId === toGroupId && e.toGroupId === fromGroupId
      );
      canvas.canvas_data.groupEdges = edges.filter((e) => e !== edge);
      if (reverse) {
        reverse.kind = "directed";
      } else {
        canvas.canvas_data.groupEdges.push({
          ...edge,
          fromGroupId: toGroupId,
          toGroupId: fromGroupId,
          kind: "directed",
        });
      }
      draft.dirtyResources.canvas = true;
    },

    deleteGroupEdge: (
      draft: Workspace,
      action: PayloadAction<{
        fromGroupId: string;
        toGroupId?: string;
        toRectIdx?: number;
      }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (!canvas?.canvas_data?.groupEdges || draft.gradingView) return;
      const { fromGroupId, toGroupId, toRectIdx } = action.payload;
      canvas.canvas_data.groupEdges = canvas.canvas_data.groupEdges.filter(
        (e) =>
          !(
            e.fromGroupId === fromGroupId &&
            e.toGroupId === toGroupId &&
            e.toRectIdx === toRectIdx
          )
      );
      draft.dirtyResources.canvas = true;
    },

    setGroupEdgeKind: (
      draft: Workspace,
      action: PayloadAction<{
        fromGroupId: string;
        toGroupId?: string;
        toRectIdx?: number;
        kind: ArrowKind;
      }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (!canvas?.canvas_data?.groupEdges || draft.gradingView) return;
      const { fromGroupId, toGroupId, toRectIdx, kind } = action.payload;
      const edge = canvas.canvas_data.groupEdges.find(
        (e) =>
          e.fromGroupId === fromGroupId &&
          e.toGroupId === toGroupId &&
          e.toRectIdx === toRectIdx
      );
      if (edge) {
        edge.kind = kind;
        draft.dirtyResources.canvas = true;
      }
    },

    setGroupEdgeCredence: (
      draft: Workspace,
      action: PayloadAction<{
        fromGroupId: string;
        toGroupId?: string;
        toRectIdx?: number;
        credence: number;
      }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (!canvas?.canvas_data?.groupEdges || draft.gradingView) return;
      const { fromGroupId, toGroupId, toRectIdx, credence } = action.payload;
      const edge = canvas.canvas_data.groupEdges.find(
        (e) =>
          e.fromGroupId === fromGroupId &&
          e.toGroupId === toGroupId &&
          e.toRectIdx === toRectIdx
      );
      if (edge) {
        edge.credence = credence;
        draft.dirtyResources.canvas = true;
      }
    },

    removeGroup: (
      draft: Workspace,
      action: PayloadAction<{ groupId: string }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (!canvas?.canvas_data || draft.gradingView) return;
      if (!canvas.canvas_data.groups) return;
      const before = canvas.canvas_data.groups.length;
      canvas.canvas_data.groups = canvas.canvas_data.groups.filter(
        (g) => g.id !== action.payload.groupId
      );
      if (canvas.canvas_data.groups.length !== before) {
        if (canvas.canvas_data.groupEdges) {
          canvas.canvas_data.groupEdges = canvas.canvas_data.groupEdges.filter(
            (e) =>
              e.fromGroupId !== action.payload.groupId &&
              e.toGroupId !== action.payload.groupId
          );
        }
        draft.dirtyResources.canvas = true;
      }
    },

    deleteGroupAndMembers: (
      draft: Workspace,
      action: PayloadAction<{ groupId: string }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (!canvas?.canvas_data || draft.gradingView) return;
      const group = canvas.canvas_data.groups?.find(
        (g) => g.id === action.payload.groupId
      );
      if (!group) return;

      const toDelete = new Set(group.memberRectIndices);
      const dropEdgesTouching = (groupId: string) => {
        if (!canvas.canvas_data!.groupEdges) return;
        canvas.canvas_data!.groupEdges =
          canvas.canvas_data!.groupEdges.filter(
            (e) => e.fromGroupId !== groupId && e.toGroupId !== groupId
          );
      };
      if (toDelete.size === 0) {
        // Empty group — same as a plain dissolve.
        canvas.canvas_data.groups = (canvas.canvas_data.groups ?? []).filter(
          (g) => g.id !== group.id
        );
        dropEdgesTouching(group.id);
        draft.dirtyResources.canvas = true;
        return;
      }

      // Build the new rectangles array + an old→new index translation
      // map. Survivors keep their relative order. Compaction is the
      // safer pattern than splicing in a loop — splice mutates the
      // array, and reusing indices mid-loop is what bugs are made of.
      const survivors: typeof canvas.canvas_data.rectangles = [];
      const indexMap = new Map<number, number>();
      canvas.canvas_data.rectangles.forEach((rect, oldIdx) => {
        if (toDelete.has(oldIdx)) return;
        indexMap.set(oldIdx, survivors.length);
        survivors.push(rect);
      });

      // Rewrite arrows on every surviving rect: drop arrows whose
      // endRectIdx is in the delete set, then remap the remaining
      // endRectIdx through the translation map.
      for (const rect of survivors) {
        rect.arrows = rect.arrows
          .filter((arrow) => !toDelete.has(arrow.endRectIdx))
          .map((arrow) => ({
            ...arrow,
            endRectIdx: indexMap.get(arrow.endRectIdx) ?? arrow.endRectIdx,
          }));
      }

      canvas.canvas_data.rectangles = survivors;

      // Drop the target group; remap every other group's member
      // indices through the same translation. Groups that lose all
      // members get pruned (matches the single-rect-delete invariant).
      if (canvas.canvas_data.groups) {
        canvas.canvas_data.groups = canvas.canvas_data.groups
          .filter((g) => g.id !== action.payload.groupId)
          .map((g) => ({
            ...g,
            memberRectIndices: g.memberRectIndices
              .filter((i) => !toDelete.has(i))
              .map((i) => indexMap.get(i) ?? i),
          }))
          .filter((g) => g.memberRectIndices.length > 0);
      }

      dropEdgesTouching(group.id);
      if (canvas.canvas_data.groupEdges) {
        const liveIds = new Set(
          (canvas.canvas_data.groups ?? []).map((g) => g.id)
        );
        canvas.canvas_data.groupEdges = canvas.canvas_data.groupEdges
          .filter(
            (e) => e.toRectIdx === undefined || !toDelete.has(e.toRectIdx)
          )
          .map((e) =>
            e.toRectIdx !== undefined
              ? { ...e, toRectIdx: indexMap.get(e.toRectIdx) ?? e.toRectIdx }
              : e
          )
          .filter(
            (e) =>
              liveIds.has(e.fromGroupId) &&
              (e.toGroupId === undefined || liveIds.has(e.toGroupId))
          );
      }

      draft.dirtyResources.canvas = true;
    },

    translateGroupMembers: (
      draft: Workspace,
      action: PayloadAction<{ groupId: string; dx: number; dy: number }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (!canvas?.canvas_data || draft.gradingView) return;
      const { groupId, dx, dy } = action.payload;
      if (dx === 0 && dy === 0) return;
      const group = canvas.canvas_data.groups?.find((g) => g.id === groupId);
      if (!group) return;
      for (const idx of group.memberRectIndices) {
        const rect = canvas.canvas_data.rectangles[idx];
        if (rect) {
          rect.x += dx;
          rect.y += dy;
        }
      }
      // If the user resized the frame manually, drag it along with the
      // members so the visual stays anchored.
      if (group.frame) {
        group.frame.x += dx;
        group.frame.y += dy;
      }
      draft.dirtyResources.canvas = true;
    },

    setGroupFrame: (
      draft: Workspace,
      action: PayloadAction<{
        groupId: string;
        frame?: import("@/types/canvasTypes").RectangleGeometry;
      }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (!canvas?.canvas_data || draft.gradingView) return;
      const group = canvas.canvas_data.groups?.find(
        (g) => g.id === action.payload.groupId
      );
      if (!group) return;
      group.frame = action.payload.frame;
      draft.dirtyResources.canvas = true;
    },

    addRectToGroup: (
      draft: Workspace,
      action: PayloadAction<{ groupId: string; rectIdx: number }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (!canvas?.canvas_data || draft.gradingView) return;
      const { groupId, rectIdx } = action.payload;
      if (
        rectIdx < 0 ||
        rectIdx >= canvas.canvas_data.rectangles.length
      )
        return;
      const groups = canvas.canvas_data.groups ?? [];
      // Enforce one-group-per-rect: refuse if rectIdx is already in
      // some group. Caller is expected to ungroup first.
      for (const g of groups) {
        if (g.memberRectIndices.includes(rectIdx)) return;
      }
      const target = groups.find((g) => g.id === groupId);
      if (!target) return;
      target.memberRectIndices = [...target.memberRectIndices, rectIdx].sort(
        (a, b) => a - b
      );
      draft.dirtyResources.canvas = true;
    },

    removeRectFromGroup: (
      draft: Workspace,
      action: PayloadAction<{ rectIdx: number }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (!canvas?.canvas_data || draft.gradingView) return;
      if (!canvas.canvas_data.groups) return;
      const { rectIdx } = action.payload;
      let changed = false;
      canvas.canvas_data.groups = canvas.canvas_data.groups
        .map((g) => {
          if (!g.memberRectIndices.includes(rectIdx)) return g;
          changed = true;
          return {
            ...g,
            memberRectIndices: g.memberRectIndices.filter(
              (i) => i !== rectIdx
            ),
          };
        })
        .filter((g) => g.memberRectIndices.length > 0);
      if (changed) draft.dirtyResources.canvas = true;
    },

    setGroupMeta: (
      draft: Workspace,
      action: PayloadAction<{
        groupId: string;
        name?: string;
        color?: string;
        evaluate?: boolean;
      }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (!canvas?.canvas_data || draft.gradingView) return;
      const group = canvas.canvas_data.groups?.find(
        (g) => g.id === action.payload.groupId
      );
      if (!group) return;
      const { name, color, evaluate } = action.payload;
      if (name !== undefined) group.name = name;
      if (color !== undefined) group.color = color;
      if (evaluate !== undefined) group.evaluate = evaluate;
      draft.dirtyResources.canvas = true;
    },

    addArrow: (
      draft: Workspace,
      action: PayloadAction<{
        startRectIdx: number;
        endRectIdx: number;
        tag?: string;
        credence?: number;
        kind?: ArrowKind;
        doNotRender?: boolean;
      }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (canvas?.canvas_data && !draft.gradingView) {
        const { startRectIdx, ...rest } = action.payload;
        canvas.canvas_data.rectangles[startRectIdx]?.arrows.push({
          tag: "",
          credence: 100,
          kind: "directed",
          ...rest,
        });
        draft.dirtyResources.canvas = true;
      }
    },

    addArrowTag: (
      draft: Workspace,
      action: PayloadAction<{
        startRectIdx: number;
        endRectIdx: number;
        textInput: string;
      }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (canvas?.canvas_data && !draft.gradingView) {
        const { startRectIdx, endRectIdx, textInput } = action.payload;
        const arrow = canvas.canvas_data.rectangles[startRectIdx]?.arrows.find(
          (a) => a.endRectIdx === endRectIdx
        );
        if (arrow) {
          arrow.tag = textInput;
          draft.dirtyResources.canvas = true;
        }
      }
    },

    addTag: (
      draft: Workspace,
      action: PayloadAction<{ idx: number; textInput: string }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (canvas?.canvas_data && !draft.gradingView) {
        const { idx, textInput } = action.payload;
        const rect = canvas.canvas_data.rectangles[idx];
        if (rect) {
          rect.tag = textInput;
          draft.dirtyResources.canvas = true;
        }
      }
    },

    // Change the category of a canvas rectangle. Mirrors changeHighlightCategory
    // so the same picker UX works on both the sidebar and the canvas.
    changeRectangleCategory: (
      draft: Workspace,
      action: PayloadAction<{ idx: number; category: string }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (canvas?.canvas_data && !draft.gradingView) {
        const { idx, category } = action.payload;
        const rect = canvas.canvas_data.rectangles[idx];
        if (rect) {
          rect.category = category;
          draft.dirtyResources.canvas = true;
        }
      }
    },

    // Sets the credence (0–100) on the arrow between two rects. Applied to
    // both directions of the edge for symmetry with the prior dashArrow
    // behavior — the credence is a property of the link, not of one
    // direction in isolation. Bidirectional arrows store credence on the
    // single arrow + its doNotRender ghost; both are updated.
    setRectangleCredence: (
      draft: Workspace,
      action: PayloadAction<{ idx: number; credence: number }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (canvas?.canvas_data && !draft.gradingView) {
        const { idx, credence } = action.payload;
        const rect = canvas.canvas_data.rectangles[idx];
        if (rect) {
          rect.credence = credence;
          draft.dirtyResources.canvas = true;
        }
      }
    },

    setArrowCredence: (
      draft: Workspace,
      action: PayloadAction<{
        startRectIdx: number;
        endRectIdx: number;
        credence: number;
      }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (canvas?.canvas_data && !draft.gradingView) {
        const { startRectIdx, endRectIdx, credence } = action.payload;
        const arrow = canvas.canvas_data.rectangles[startRectIdx]?.arrows.find(
          (a) => a.endRectIdx === endRectIdx
        );
        if (arrow) {
          arrow.credence = credence;
        }
        const revArrow = canvas.canvas_data.rectangles[endRectIdx]?.arrows.find(
          (a) => a.endRectIdx === startRectIdx
        );
        if (revArrow) {
          revArrow.credence = credence;
        }
        draft.dirtyResources.canvas = true;
      }
    },

    deleteArrow: (
      draft: Workspace,
      action: PayloadAction<{ startRectIdx: number; endRectIdx: number }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (canvas?.canvas_data && !draft.gradingView) {
        const { startRectIdx, endRectIdx } = action.payload;
        const arrowIdx = canvas.canvas_data.rectangles[startRectIdx]?.arrows.findIndex(
          (a) => a.endRectIdx === endRectIdx
        );
        if (arrowIdx !== undefined && arrowIdx !== -1) {
          canvas.canvas_data.rectangles[startRectIdx]?.arrows.splice(arrowIdx, 1);
        }
        const revArrowIdx = canvas.canvas_data.rectangles[endRectIdx]?.arrows.findIndex(
          (a) => a.endRectIdx === startRectIdx
        );
        if (revArrowIdx !== undefined && revArrowIdx !== -1) {
          canvas.canvas_data.rectangles[endRectIdx]?.arrows.splice(revArrowIdx, 1);
        }
        draft.dirtyResources.canvas = true;
      }
    },

    // Like deleteArrow but only removes the arrow in the specified direction
    // (does not also delete the reverse). Used by pair-mode menu operations
    // that want to keep one half of a parallel pair.
    deleteSingleArrow: (
      draft: Workspace,
      action: PayloadAction<{ startRectIdx: number; endRectIdx: number }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (canvas?.canvas_data && !draft.gradingView) {
        const { startRectIdx, endRectIdx } = action.payload;
        const arrowIdx = canvas.canvas_data.rectangles[startRectIdx]?.arrows.findIndex(
          (a) => a.endRectIdx === endRectIdx
        );
        if (arrowIdx !== undefined && arrowIdx !== -1) {
          canvas.canvas_data.rectangles[startRectIdx]?.arrows.splice(arrowIdx, 1);
          draft.dirtyResources.canvas = true;
        }
      }
    },

    // Toggle Parsons evaluation mode on the currently selected canvas.
    setEvaluationMode: (
      draft: Workspace,
      action: PayloadAction<{ enabled: boolean }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (canvas?.canvas_data && !draft.gradingView) {
        canvas.canvas_data.evaluation_mode = action.payload.enabled;
        draft.dirtyResources.canvas = true;
      }
    },

    // Atomic kind change on a single arrow (identified by start/end indices).
    setArrowKind: (
      draft: Workspace,
      action: PayloadAction<{
        startRectIdx: number;
        endRectIdx: number;
        kind: ArrowKind;
      }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (canvas?.canvas_data && !draft.gradingView) {
        const { startRectIdx, endRectIdx, kind } = action.payload;
        const arrow = canvas.canvas_data.rectangles[startRectIdx]?.arrows.find(
          (a) => a.endRectIdx === endRectIdx
        );
        if (arrow) {
          arrow.kind = kind;
          draft.dirtyResources.canvas = true;
        }
      }
    },

    doubleArrow: (
      draft: Workspace,
      action: PayloadAction<{ startRectIdx: number; endRectIdx: number }>
    ) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (canvas?.canvas_data && !draft.gradingView) {
        const { startRectIdx, endRectIdx } = action.payload;
        const arrow = canvas.canvas_data.rectangles[startRectIdx]?.arrows.find(
          (a) => a.endRectIdx === endRectIdx
        );
        if (arrow) {
          arrow.kind = "bidirectional";
          canvas.canvas_data.rectangles[endRectIdx]?.arrows.push({
            ...arrow,
            endRectIdx: startRectIdx,
            doNotRender: true,
          });
          draft.dirtyResources.canvas = true;
        }
      }
    },

    resetCanvas: (draft: Workspace) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (canvas?.canvas_data && !draft.gradingView) {
        canvas.canvas_data.rectangles = [];
        draft.dirtyResources.canvas = true;
      }
    },

    scaleCanvas: (draft: Workspace, action: PayloadAction<{ scale: number }>) => {
      const canvas = draft.canvases.find(
        (c) => c.canvas_id === draft.selectedCanvasId
      );
      if (canvas?.canvas_data && !draft.gradingView) {
        canvas.canvas_data.scale = action.payload.scale;
        draft.dirtyResources.canvas = true;
      }
    },

    // ========================================================================
    // PAPER MUTATIONS (operate on currently selected paper)
    // ========================================================================

    setPaperState: (draft: Workspace, action: PayloadAction<any>) => {
      const paper = draft.papers.find(
        (p) => p.paper_id === draft.selectedPaperId
      );
      if (paper?.paper_data) {
        paper.paper_data.editorState = action.payload;
        draft.dirtyResources.paper = true;
      }
    },

    // ========================================================================
    // WORKSPACE DATA
    // ========================================================================

    setWorkspaceData: (
      draft: Workspace,
      action: PayloadAction<{
        workspace_id: string;
        workspace_name: string;
        shared_data: SharedDataJSON;
        user_data: UserDataJSON;
        workspace_permissions?: WorkspacePermissionWithAuthor[];
      }>
    ) => {
      const { workspace_id, workspace_name, shared_data, user_data, workspace_permissions } =
        action.payload;
      draft.workspace_id = workspace_id;
      draft.workspace_name = workspace_name;
      // Legacy workspaces (created before defaults were enforced server-side)
      // or stale persisted state may arrive with shared_data.categories ==
      // null. Fall back to initial defaults so every annotation surface
      // remains functional.
      const incomingCategories = shared_data?.categories;
      draft.shared_data = {
        ...(shared_data ?? {}),
        categories:
          incomingCategories && incomingCategories.length > 0
            ? incomingCategories
            : initialState.shared_data.categories,
      };
      draft.user_data = user_data;
      // Stage 3.7: store membership for client-side role derivation
      // (selectIsCurrentUserWorkspaceOwner). Absent payloads leave the
      // existing array in place so partial setWorkspaceData calls don't
      // wipe it.
      if (workspace_permissions !== undefined) {
        draft.workspace_permissions = workspace_permissions;
      }
    },
  },
});

export const {
  setWorkspaceId,
  setWorkspaceName,
  setSharedData,
  addCategory,
  removeCategory,
  overwriteCategories,
  setUserData,
  addReading,
  setReadings,
  removeReading,
  renameReading,
  addCanvas,
  setCanvases,
  setCanvasData,
  removeCanvas,
  renameCanvas,
  addPaper,
  setPapers,
  removePaper,
  renamePaper,
  setWorkspaceData,
  // Selection actions
  setSelectedReading,
  setSelectedCanvas,
  setSelectedPaper,
  clearAllSelections,
  // Dirty tracking
  markResourceDirty,
  markResourceClean,
  markAllResourcesClean,
  // Reading mutations
  addHighlight,
  deleteHighlight,
  changeHighlightCategory,
  changeHighlightComment,
  updateHighlight,
  resetHighlights,
  addDemoHighlights,
  setReadingHighlights,
  // Grading view (Stage 3.7 + Stage 6.8 shared picker)
  setGradingView,
  clearGradingView,
  setGradingViewEvaluation,
  setGradeWeights,
  setGradeDimensionWeight,
  setEditorRubricCriteria,
  setReadingGradingViewData,
  setCanvasGradingViewData,
  setReadingContributorsCache,
  setCanvasContributorsCache,
  markAssessmentSaved,
  // Rubric mutations
  setRubricCompleteness,
  setRubricCategoryMismatch,
  setMatchThreshold,
  setRubricUseRecommended,
  loadRubric,
  setExpertHighlights,
  loadCanvasRubric,
  setExpertCanvas,
  markCanvasAssessmentSaved,
  // Canvas mutations
  addRectangle,
  deleteRectangle,
  deleteRectanglesBulk,
  translateRectanglesBulk,
  moveRectangle,
  editRectangle,
  toggleRectangleExpansion,
  groupRectangles,
  removeGroup,
  addGroupEdge,
  flipGroupEdge,
  deleteGroupEdge,
  setGroupEdgeKind,
  setGroupEdgeCredence,
  deleteGroupAndMembers,
  translateGroupMembers,
  setGroupFrame,
  setGroupMeta,
  addRectToGroup,
  removeRectFromGroup,
  addArrow,
  addArrowTag,
  addTag,
  changeRectangleCategory,
  setRectangleCredence,
  setArrowCredence,
  deleteArrow,
  deleteSingleArrow,
  setArrowKind,
  setEvaluationMode,
  doubleArrow,
  resetCanvas,
  scaleCanvas,
  // Paper mutations
  setPaperState,
} = workspaceSlice.actions;

// Selectors
export const selectCurrentReading = createSelector(
  [(state: RootState) => state.workspace.readings, (state: RootState) => state.workspace.selectedReadingId],
  (readings, selectedId) => readings.find((r) => r.reading_id === selectedId) || null
);

export default workspaceSlice.reducer;
